import { useCallback, useEffect, useRef } from 'react';
import { ARTICLE_IN_IFRAME_URL_SEARCH_PARAM } from 'utils/constants/urlSearchParameters';
import { usePopstate } from 'utils/hooks/usePopstate';
import { useObjectRef } from 'utils/hooks/useObjectRef';
import { ArticleInIframe } from 'components/ArticleInIframe';

const openArticleInIframe = ({ src, onClose }) => {
  const iframe = new ArticleInIframe();
  iframe.show({
    src,
    onClose,
  });

  return iframe;
};

export const useOpenArticleInIframe = ({ onClose } = {}) => {
  const currentIframeRef = useRef(null);

  const onCloseRef = useObjectRef(onClose);

  const handleClose = useCallback(() => {
    currentIframeRef.current = null;
    if (onCloseRef.current) {
      onCloseRef.current();
    }
  }, [onCloseRef]);

  usePopstate(useCallback(() => {
    const { current: currentIframe } = currentIframeRef;
    if (currentIframe) {
      currentIframeRef.current = null;
      currentIframe.hide();
    }
    const iframeSearchParam = new URLSearchParams(window.location.search).get(ARTICLE_IN_IFRAME_URL_SEARCH_PARAM);
    if (!iframeSearchParam) {
      return;
    }
    currentIframeRef.current = openArticleInIframe({
      src: decodeURIComponent(iframeSearchParam),
      onClose: handleClose,
    });
  }, [handleClose]));

  useEffect(() => () => {
    const { current: currentIframe } = currentIframeRef;
    if (currentIframe) {
      currentIframeRef.current = null;
      currentIframe.hide();
    }
  }, []);

  return useCallback((articleUrl) => {
    if (currentIframeRef.current) {
      // should be invariant
      currentIframeRef.current.hide();
      currentIframeRef.current = null;
    }
    currentIframeRef.current = openArticleInIframe({
      src: articleUrl,
      onClose: handleClose,
    });
  }, [handleClose]);
};
